<script setup>
import { ref, computed, onMounted, provide, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import _ from 'lodash'
import SubscriptionService from '@/services/SubscriptionService'
import subscribePlansDescription from '@/components/Subscribe/subscribePlansDescription'
import SubscribePlans from '@/components/Subscribe/SubscribePlans.vue'
import SubscribePlansCompare from '@/components/Subscribe/SubscribePlansCompare.vue'
import SubscribePlanCart from '@/components/Subscribe/SubscribePlanCart.vue'
import Spinner from '../components/ui/Spinner.vue'
import FormButton from '../components/Atoms/FormButton.vue'
import { track, EVENTS } from '@/lib/tracking'

const store = useStore()
const route = useRoute()
const router = useRouter()

const yearly = ref(true)

const packCategories = ref([
  { label: 'Live Stream', value: 'live' },
  { label: 'multistream', value: 'restream' },
])
const packCategory = ref({ label: 'Live Stream', value: 'live' })
const AvailablePaymentMethods = [{ label: 'Fastspring', value: 'fastspring-contextual' }]

const subscriptionPackages = ref([])
const subscriptionPackage = ref(null)
const paymentMethodsVisible = ref(true)
const hiddenPack = ref(false)
const error = ref(null)
const loading = ref(false)
const productType = ref('streaming')

const availablePaymentMethods = ref(AvailablePaymentMethods)
const selectedPaymentMethod = ref(availablePaymentMethods.value[0])

const updateSelectedPaymentMethod = (method) => {
  selectedPaymentMethod.value = method
}

const selectPackage = (pack) => {
  subscriptionPackage.value = pack
  router.push({ query: { package: pack?._id } })
}

const selectPaymentMethod = (paymentMethod) => {
  selectedPaymentMethod.value = paymentMethod
}
provide('selectedPaymentMethod', {
  selectedPaymentMethod,
  selectPaymentMethod,
  selectPackage,
  availablePaymentMethods,
  updateSelectedPaymentMethod
})

const baseSubscription = computed(() => store.getters['User/baseSubscription'])

const loadPackages = async () => {
  store.dispatch('Ui/setLoadingStatus', true)
  error.value = null
  try {
    // Fetch available subscriptions packages. The API returns them ordered by
    // baseCharge, which is the order the plan grid presents them in.
    const packages = await SubscriptionService.getSubscriptionPackages()

    subscriptionPackages.value = packages
    const paymentMethods = await SubscriptionService.getUserPaymentMethods()
    selectedPaymentMethod.value = paymentMethods[0]
    availablePaymentMethods.value = paymentMethods

    if (!_.isEmpty(paymentMethods) && paymentMethods.length === 1) {
      paymentMethodsVisible.value = false
    }

    // Set selected subscription
    let packageId = route.query?.package
    const showPromotionPackageOnly = route.query?.ppo

    if (packageId && !hiddenPack.value) {
      subscriptionPackage.value = _.find(plansData.value, { _id: packageId })
      if (_.isEmpty(subscriptionPackage.value) || !subscriptionPackage.value) {
        subscriptionPackage.value = await SubscriptionService.getSubscriptionPackages(packageId)
        hiddenPack.value = true
      }

      // Update plan category
      setTimeout(() => {
        const packCat = _.find(packCategories.value, { value: subscriptionPackage.value?.category })
        if (packCat) { packCategory.value = packCat }
        else { packCategory.value = packCategories.value[0] }
        // if (packCat) {
        //   this.selectSubscriptionPackageCategory(packCat, false)
        // }
        if (showPromotionPackageOnly === 'true') {
          subscriptionPackages.value = [subscriptionPackage.value]
          packCategories.value = [packCat]
        }
      }, 100)
    } else {
      // Read url query params
      // this.processURLSearchParams()
    }

    // this.updateUserBaseSubscription()
    let baseSub = baseSubscription.value

    const action = route.query?.action
    const baseCharge = parseFloat(route.query?.bycharge)

    if (action === 'upgrade' || (subscriptionPackage.value?.baseCharge === 0)) {
      const userPackage = baseSub?.package || { baseCharge: 0, cycleAge: 30 }
      let superiorPackage = _.find(packages, p => {
        let minBaseCharge = Math.max(userPackage.baseCharge, 9.99)
        let minCycleAge = Math.max(userPackage.cycleAge, 30)
        return p.cycleAge === minCycleAge && p.baseCharge > minBaseCharge
      })

      if (!superiorPackage) { superiorPackage = _.find(packages, p => p.baseCharge === userPackage.baseCharge) }
      if (superiorPackage) {
        subscriptionPackage.value = superiorPackage
      }
    } else if (action === 'resubscribe') {
      const userPackage = baseSub?.package || { baseCharge: 0 }
      const curPack = _.find(packages, p => p.baseCharge === userPackage.baseCharge)
      subscriptionPackage.value = curPack
    } else if (baseCharge) {
      const isAnnualPack = route.query?.annual === '1'

      const subPack = _.find(packages, p => {
        let bool = p.baseCharge === baseCharge
        if (isAnnualPack) {
          bool = bool && /year|annual/gi.test(p.name)
        }

        return bool
      })
      if (subPack) {
        subscriptionPackage.value = subPack
      }
    }
  } catch (e) {
    error.value = e
  } finally {
    store.dispatch('Ui/setLoadingStatus', false)
  }
}

const retryLoadPackages = async () => {
  loading.value = true
  await loadPackages()
  loading.value = false
}

const serverPackages = computed(() => {
  if (yearly.value && productType.value === 'streaming') {
    return subscriptionPackages.value.filter(pack => ((pack.cycleAge === 365) && (pack.category !== 'ott')))
  } else if (yearly.value && productType.value === 'ott') {
    return subscriptionPackages.value.filter(pack => ((pack.cycleAge === 365) && (pack.category === 'ott')))
  } else if (productType.value === 'ott') {
    return subscriptionPackages.value.filter(pack => ((pack.cycleAge === 30) && (pack.category === 'ott')))
  } else {
    return subscriptionPackages.value.filter(pack => ((pack.cycleAge === 30) && (pack.category !== 'ott')))
  }
})

const normalizeOttNames = (info) => {
  if (info?.category === 'ott' && info?.name) {
    return {
      ...info,
      name: info.name.replace('OTT - ', '').replace(' - Yearly', '')
    }
  }
  return info
}

const normalizeInfoOttNames = (info) => {
  if (info?.name?.includes('OTT')) {
    return {
      ...info,
      name: info.name.replace('OTT - ', '').replace(' - Yearly', '')
    }
  }
  return info
}

const normalizePlanName = (name) => (name || '').replace(/\s+Yearly\s+-/, ' -')

const plansData = computed(() => {
  return serverPackages.value.map(pack => ({
    ...normalizeOttNames(pack),
    charectiristics: subscribePlansDescription.find(plan => {
      if (yearly.value && productType.value === 'streaming') {
        return normalizePlanName(plan.info.name) === normalizePlanName(pack.name) ||
          (plan.info.name + ' - Yearly' === pack.name) || (plan.info.name + ' Yearly' === pack.name) || (plan.info.name + ' Yearly - 2026' === pack.name)
      } else if (productType.value === 'ott' && !yearly.value) {
        return plan.info.name === pack.name + ' - Yearly'
      } else {
        return normalizePlanName(plan.info.name) === normalizePlanName(pack.name)
      }
    })?.charectiristics,
    info: normalizeInfoOttNames(subscribePlansDescription.find(plan => {
      if (yearly.value && productType.value === 'streaming') {
        return normalizePlanName(plan.info.name) === normalizePlanName(pack.name) ||
          (plan.info.name + ' - Yearly' === pack.name) || (plan.info.name + ' Yearly' === pack.name) || (plan.info.name + ' Yearly - 2026' === pack.name)
      } else if (productType.value === 'ott' && !yearly.value) {
        return plan.info.name === pack.name + ' - Yearly'
      } else {
        return plan.info.name === pack.name
      }
    })?.info),
  }))
})

const isModerator = computed(() => {
  return store.getters['User/isTeamModerator']
})

const ownerEmail = computed(() => {
  return store.state.User.guestData.ownersEmail
})

const shouldShowCompare = computed(() => route.name === 'Payments')

// Hooks
onMounted(async () => {
  loading.value = true
  await loadPackages()
  if (route.path === '/ott-apps') productType.value = 'ott'
  if (subscriptionPackage.value?.category === 'ott') {
    productType.value = 'ott'
    subscriptionPackage.value = _.find(plansData.value, { _id: subscriptionPackage.value._id })
  }
  loading.value = false

  if (!subscriptionPackage.value) {
    track(EVENTS.PLANS_CATALOG_VIEWED, {
      productType: productType.value,
      planCount: plansData.value.length,
      routeAction: route.query?.action,
    })
  }
})

// Watch
watch(subscriptionPackage, () => {
  !hiddenPack.value && subscriptionPackage.value?.cycleAge === 30 ? yearly.value = false : yearly.value = true
  !hiddenPack.value && (subscriptionPackage.value = _.find(plansData.value, { _id: subscriptionPackage.value?._id }))
})

</script>

<template>
  <div class="container px-3 sm:px-4">
    <template v-if="loading">
      <main class="w-full h-[80vh] flex items-center justify-center">
        <Spinner />
      </main>
    </template>
    <template v-else-if="isModerator">
      <div class="mt-8 lg:mt-64 mx-auto">
        <h3 class="text-lg text-center text-surface-9 mb-2">Moderators don't have access to the Upgrade page.</h3>
        <p class="text-surface-6 text-center text-sm mb-4">Only the team owner and admins can access this page. Contact your team owner (<a class="underline" :href="'mailto:' + ownerEmail">{{ ownerEmail }}</a>)
          for more information.</p>
        <p class="text-center">
          <FormButton to="/">
            Go to Livestreams
          </FormButton>
        </p>
      </div>
    </template>
    <template v-else-if="subscriptionPackage">
      <main class="mt-6 sm:mt-12">
        <SubscribePlanCart :subscription-package="subscriptionPackage" :is-yearly="yearly" />
      </main>
    </template>
    <template v-else>
      <header v-if="shouldShowCompare" class="mt-8 sm:mt-12 mb-6">
        <h1 class="text-center font-medium text-xl break-words">Upgrade your subscription</h1>
      </header>
      <main>
        <div v-if="error" class="bg-surface-2 ring-1 ring-surface-3 rounded-xl py-12 px-4 flex flex-col items-center gap-3">
          <h3 class="text-surface-9 font-medium">We couldn't load the subscription plans.</h3>
          <p class="text-surface-6 text-sm text-center">Something went wrong on our side. Please try again, or contact
            support if the problem continues.</p>
          <FormButton label="Try again" @click="retryLoadPackages" />
        </div>
        <template v-else>
          <SubscribePlans :product-type="productType" class="mb-10" :plans-data="plansData" v-model:yearly="yearly"
            :should-show-compare="shouldShowCompare" />
          <SubscribePlansCompare v-if="shouldShowCompare" :product-type="productType" :plans-data="plansData"
            v-model:yearly="yearly" />
        </template>
      </main>
    </template>
  </div>
</template>

<style>
html {
  scroll-behavior: smooth;
}
</style>
<!-- Add "scoped" attribute to limit CSS to this component only -->
